import React, { useEffect, useState, useCallback } from "react";
import { api, formatMoney, formatDateTime, formatApiError } from "@/lib/api";
import { UploadCloud, FileSpreadsheet, Trash2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function OpenInvoices() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [summary, setSummary] = useState({ count: 0, total_amount: 0, last_upload: null });
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/open-invoices/summary");
            setSummary(r.data);
        } catch (err) {
            // ignore
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function submit(e) {
        e.preventDefault();
        if (!file) {
            toast.error("Dosya seçin");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/import/open-invoices?purge_first=true", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            toast.success(`${r.data.added} açık fatura yüklendi`);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    }

    async function clearAll() {
        if (!window.confirm("Tüm açık fatura kayıtları silinsin mi?")) return;
        try {
            await api.delete("/open-invoices");
            toast.success("Silindi");
            setResult(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    }

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6">
                <div className="overline">Bölüm 07</div>
                <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">
                    Açık Faturalar
                </h1>
                <p className="text-sm text-neutral-500 mt-3 max-w-3xl">
                    Vade tarihine göre gelecek aylarda tahsil edilmesi beklenen açık (kalan) faturaları Excel olarak yükleyin.
                    Bu veri, <strong>Tahsilat Raporu</strong>'nun sağ tarafında (Ödemesi Gelecek Faturaların Aylara Göre Dağılımı) kullanılır.
                    <br />
                    Beklenen kolonlar: <span className="font-mono text-[11px]">HESAP KODU · FATURA NO · TUTAR (veya KALAN) · VADE · <strong>FATURA TARIHI (ops.)</strong> · ADI (ops.) · AÇIKLAMA (ops.)</span>
                </p>
            </header>

            <div className="px-10 py-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-black/10">
                <StatBox label="Yüklü Kayıt" value={summary.count} testid="oi-count" />
                <StatBox label="Toplam Tutar" value={`₺ ${formatMoney(summary.total_amount)}`} tone="debit" testid="oi-total" />
                <StatBox label="Son Yükleme" value={summary.last_upload ? formatDateTime(summary.last_upload) : "—"} testid="oi-last" small />
            </div>

            <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
                <form onSubmit={submit} className="lg:col-span-3 border border-black/15 p-6 space-y-5">
                    <label className="block">
                        <span className="overline block mb-2">Dosya *</span>
                        <div className="border border-dashed border-black/25 p-8 text-center bg-neutral-50">
                            <FileSpreadsheet className="mx-auto text-neutral-500" size={28} />
                            <input
                                data-testid="oi-file"
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="block mx-auto mt-4 text-xs font-mono"
                            />
                            {file && (
                                <div className="text-xs font-mono text-neutral-600 mt-2">Seçildi: {file.name}</div>
                            )}
                        </div>
                    </label>

                    <div className="border border-brand-red/30 bg-red-50 p-3 flex items-start gap-2">
                        <AlertTriangle size={14} className="text-brand-red mt-0.5 shrink-0" />
                        <p className="text-[11px] text-neutral-700 leading-relaxed">
                            Yükleme <strong>mevcut tüm açık fatura kayıtlarını siler</strong> ve yeni dosyayla değiştirir.
                            Bu şekilde her ay güncel liste tek dosyayla değiştirilebilir.
                            {!isAdmin && (
                                <span className="text-brand-red block mt-1">
                                    Bu işlem yalnızca yöneticiye açıktır.
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <button
                            data-testid="oi-submit"
                            disabled={uploading || !isAdmin}
                            className="btn-sharp flex-1 justify-center"
                        >
                            <UploadCloud size={14} />
                            {uploading ? "Yükleniyor…" : "Yükle ve Değiştir"}
                        </button>
                        {summary.count > 0 && isAdmin && (
                            <button
                                type="button"
                                data-testid="oi-clear"
                                onClick={clearAll}
                                className="btn-outline"
                                title="Tümünü sil"
                            >
                                <Trash2 size={13} />
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="oi-refresh"
                            onClick={load}
                            className="btn-outline"
                            title="Yenile"
                        >
                            <RefreshCw size={13} />
                        </button>
                    </div>
                </form>

                <div className="lg:col-span-2">
                    {result && (
                        <div className="border border-black/15 p-4 mb-6" data-testid="oi-result">
                            <div className="overline mb-2">Sonuç</div>
                            <div className="text-sm">
                                Yüklenen: <span className="font-mono-num font-semibold">{result.added}</span>
                            </div>
                            {result.purged && (
                                <div className="text-[11px] font-mono text-brand-red mt-1">
                                    ⚠ Eski kayıtlar silindi
                                </div>
                            )}
                            {result.errors?.length > 0 && (
                                <div className="mt-3">
                                    <div className="overline text-brand-red">Hatalar</div>
                                    <ul className="text-xs font-mono text-brand-red mt-1 list-disc pl-5 space-y-1 max-h-32 overflow-auto">
                                        {result.errors.map((e, i) => (
                                            <li key={i}>{e}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                    <h3 className="font-display text-lg font-bold tracking-tight mb-3">Örnek Kolonlar</h3>
                    <ul className="text-[10px] font-mono text-neutral-600 space-y-1 list-disc pl-4">
                        <li>HESAP KODU · ADI (ops.)</li>
                        <li>FATURA NO (ops.)</li>
                        <li>TUTAR (veya KALAN, BAKİYE BORC)</li>
                        <li>VADE (13.01.2026 veya 2026-01-13)</li>
                        <li><strong>FATURA TARIHI (ops.)</strong> — Tahsilat Ekranı'nda "Fatura Ayı" satırını doldurur</li>
                        <li>ACIKLAMA (ops.)</li>
                    </ul>
                    <p className="text-[10px] font-mono text-neutral-500 mt-3 leading-relaxed">
                        HESAP KODU müşteri kodu ile eşleşmezse fatura yine yüklenir; raporda eşleşmediği için görünmeyebilir.
                    </p>
                </div>
            </div>
        </div>
    );
}

function StatBox({ label, value, tone, testid, small }) {
    const color = tone === "debit" ? "text-brand-red" : "text-brand-ink";
    return (
        <div className="border border-black/10 p-4" data-testid={testid}>
            <div className="overline">{label}</div>
            <div className={`font-mono-num font-display font-bold mt-1 ${color} ${small ? "text-lg" : "text-2xl"}`}>{value}</div>
        </div>
    );
}
